'use no memo';
import type { Extension } from '@confluence/macro-tracker/entry-points/types.ts';
import { canUseMultiMacroQuery } from '@confluence/fabric-extension-queries/entry-points/useMultiMacroQuery.ts';
import { useMultiMacroQuery } from '@confluence/fabric-extension-queries/entry-points/multiMacroQuery.ts';
import { getMacroQueryVariables } from '@confluence/fabric-extension-queries/entry-points/getMacroQueryVariables.ts';
import { LegacyMacroRendererQueryBodyQuery } from '@confluence/fabric-extension-queries/entry-points/LegacyMacroRendererQueryBodyQuery.graphql.ts';
import { LegacyMacroRendererQueryContentQuery } from '@confluence/fabric-extension-queries/entry-points/LegacyMacroRendererQueryContentQuery.graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- preserving original untyped behavior for backwards compatibility
export const useIndividualMacroContentData = (contentId: string, node: Extension): any => {
	const { isMacroContentAndBody, ...rest } = getMacroQueryVariables({
		node,
		contentId,
	});

	const MacroQuery = isMacroContentAndBody
		? LegacyMacroRendererQueryBodyQuery
		: LegacyMacroRendererQueryContentQuery;

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types -- preserving original behavior
	const { data } = useQueryInterop(MacroQuery, {
		fetchPolicy: 'cache-only',
		variables: rest,
	});

	return data;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- preserving original untyped behavior for backwards compatibility
export const useMultiMacroContentData = (contentId: string, node: Extension): any => {
	const macroId = node.parameters?.macroMetadata?.macroId?.value;
	const { renderedMacro } = useMultiMacroQuery({ contentId, macroId });

	return {
		macroBodyRenderer: renderedMacro,
		representation: renderedMacro?.macroRenderedRepresentation,
		mediaToken: renderedMacro?.mediaToken,
		webResourceDependencies: renderedMacro?.webResource,
		macroBodyStorageFormat: { value: renderedMacro?.macroBodyStorage },
	};
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- preserving original untyped behavior for backwards compatibility
export const useMacroContentData = (contentId: string, node: any): any => {
	return canUseMultiMacroQuery(node.extensionKey)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useMultiMacroContentData(contentId, node)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useIndividualMacroContentData(contentId, node);
};
